import { Stack } from 'component-library';
import { Trans, useTranslation } from 'react-i18next';

import { BenchmarkType, PlotReportFactElement, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { CapsuleTrend } from '@/components';
import { GraphFact } from '@/components/Charts/types';
import { NCCardSectionHeader, printLatestDate, printNthYearDate } from '@/components/NCCard/NCCard';
import { getNCChangeIcon, getNCChangeIconVariant, NCFactCapsule } from '@/components/NCFactCapsule/NCFactCapsule';
import { DeforestationBenchmarkPill } from '@/components/Pill/BenchmarkPill';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { isNullish } from '@/utils/booleans';
import { formatUnit } from '@/utils/formatting';

export type DeforestationCardStatsTileProps = {
  latestAnalysisFact: PlotReportFactElement<number | null> | undefined;
  oneYearUpliftFact: PlotReportFactElement<number | null> | undefined;
  oneYearPctChangeFact: PlotReportFactElement<number | null> | undefined;
  benchmarkFact: PlotReportFactElement<GraphFact[] | null> | undefined;
};

export const DeforestationCardStatsTile = ({
  latestAnalysisFact,
  oneYearUpliftFact,
  oneYearPctChangeFact,
  benchmarkFact,
}: DeforestationCardStatsTileProps) => {
  const { t } = useTranslation();

  const deforestationTrendDisplay = !isNullish(oneYearPctChangeFact?.value)
    ? `${getDisplayNumber(oneYearPctChangeFact?.value ?? 0, undefined, {
        signDisplay: 'always',
      })} %`
    : '-';

  const latestAnalysisDisplay = !isNullish(latestAnalysisFact?.value)
    ? `${getDisplayNumber(latestAnalysisFact?.value ?? 0)} ${formatUnit(latestAnalysisFact?.unit)}`
    : '-';

  const lastMeasuredDate = printLatestDate([latestAnalysisFact?.measured_at]);
  const oneYearChangePreviousMeasuredAt = printNthYearDate([oneYearUpliftFact?.measured_at], -1);

  const benchmkarks =
    benchmarkFact?.value?.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()) ?? [];

  const benchmarkType = benchmkarks
    ?.at(-1)
    ?.labels?.find((label) => label.name === PlotReportFactLabelNameEnum.benchmark)?.value as BenchmarkType | undefined;

  return (
    <Stack className='justify-center gap-8 p-6 lg:gap-4 lg:p-8 lg:pr-2' data-testid='deforestation-stats-tile'>
      <div className='flex flex-col gap-8 lg:gap-4'>
        <NCCardSectionHeader
          title={t('shared.ncaDetail.details.deforestation.labels.deforestationStats')}
          supplementaryInfo={`${t('shared.ncaDetail.details.labels.updated')} ${lastMeasuredDate}`}
          popover={{
            title: t('shared.ncaDetail.details.deforestation.explainers.deforestation.title'),
            body: t('shared.ncaDetail.details.deforestation.explainers.deforestation.body'),
          }}
        />
        <Stack direction='row' className='flex-wrap gap-6'>
          <NCFactCapsule
            label={t('shared.ncaDetail.details.labels.latestAnalysis')}
            fact={latestAnalysisFact}
            trend={
              typeof oneYearPctChangeFact?.value === 'number' && (
                <CapsuleTrend variant={oneYearPctChangeFact.value < 0 ? 'positive' : 'negative'}>
                  {deforestationTrendDisplay}
                </CapsuleTrend>
              )
            }
            capsuleIconProps={{
              icon: getNCChangeIcon(latestAnalysisFact),
              variant: getNCChangeIconVariant(latestAnalysisFact),
            }}
            infoPopoverProps={{
              title: t('shared.ncaDetail.details.explainers.latestAnalysis.title'),
              body: t('shared.ncaDetail.details.explainers.latestAnalysis.body', { date: lastMeasuredDate }),
              popoverTriggerProps: {
                className: 'text-xs',
              },
            }}
          />
          {!isNullish(oneYearUpliftFact?.value) && (
            <NCFactCapsule
              label={t('shared.ncaDetail.details.labels.oneYearChange')}
              fact={oneYearUpliftFact}
              capsuleIconProps={{
                icon: getNCChangeIcon(oneYearUpliftFact),
                variant: getNCChangeIconVariant(oneYearUpliftFact),
              }}
              infoPopoverProps={{
                title: t('shared.ncaDetail.details.deforestation.explainers.oneYearChange.title'),
                body: t('shared.ncaDetail.details.deforestation.explainers.oneYearChange.body', {
                  date: lastMeasuredDate,
                  previousDate: oneYearChangePreviousMeasuredAt,
                }),
                popoverTriggerProps: {
                  'data-testid': 'deforestation-since-previous-analysis-tooltip',
                },
              }}
            />
          )}
          {benchmarkType && (
            <Stack className='mt-4 gap-4 rounded-2xl bg-neutral-black-4 px-4 py-6'>
              <DeforestationBenchmarkPill benchmarkType={benchmarkType} />
              <span className='typography-body1Bold'>
                {t('shared.ncaDetail.details.deforestation.labels.whatDoesThisMean')}
              </span>
              <Trans
                i18nKey={`shared.ncaDetail.details.deforestation.explainers.benchmark.${benchmarkType}`}
                tOptions={{ value: latestAnalysisDisplay }}
                components={{
                  p: <p className='typography-body1 text-text-secondary' />,
                }}
              />
            </Stack>
          )}
        </Stack>
      </div>
    </Stack>
  );
};
